import { useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { api } from "../api/client";
import type {
  ChannelRow,
  ClientDetail,
  GmailSenderSettings,
  MarketRow,
  Meta,
  SubscriptionRow,
} from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { useMutation, useResource } from "../hooks/useResource";
import { ClientUsersPanel } from "../components/ClientUsersPanel";
import {
  ConfirmDialog,
  ErrorBanner,
  LoadingState,
  Mono,
  PageHeader,
  RoleGate,
  SecretField,
  when,
} from "../components/common";
import {
  EmailChannelFields,
  emailChannelExtra,
  emailFormState,
  gmailSenderState,
  readEmailChannel,
  validateEmailForm,
  type EmailFormState,
  type GmailSenderState,
} from "../components/EmailChannelForm";

/**
 * One client: markets, owned zones and delivery channels — the four inserts that
 * "onboard a client" used to mean, on one screen.
 *
 * A channel's destination URL is write-only by design. The console can tell you a
 * URL is configured and which host it points at; reading it back would put a live
 * Slack credential in a browser tab, so the API does not offer it and this screen
 * cannot ask.
 */
export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const detail = useResource<ClientDetail>(id ? `/api/clients/${id}` : null);
  const markets = useResource<{ items: MarketRow[] }>("/api/markets");
  const meta = useResource<Meta>("/api/meta");
  // Read-only here. The account is server-wide and is managed on /settings; this
  // screen fetches it so a Gmail channel can say what it sends as, and so an
  // operator is not left guessing why one is inactive.
  const gmailSender = useResource<GmailSenderSettings>("/api/settings/gmail");
  const sender = gmailSenderState(gmailSender.data);

  const [toast, setToast] = useState<string | null>(null);
  const [subscribing, setSubscribing] = useState(false);
  const [addingChannel, setAddingChannel] = useState(false);
  const [editingChannel, setEditingChannel] = useState<ChannelRow | null>(null);
  const [zoneFor, setZoneFor] = useState<SubscriptionRow | null>(null);
  const [editingClient, setEditingClient] = useState(false);
  const [deletingClient, setDeletingClient] = useState(false);

  const toggleSub = useMutation(async (sub: SubscriptionRow) => {
    await api.put(`/api/subscriptions/${sub.id}`, { active: !sub.active });
  });
  const setQuota = useMutation(async (args: { sub: SubscriptionRow; perDay: number | null }) => {
    await api.put(`/api/subscriptions/${args.sub.id}`, {
      quota: args.perDay === null ? {} : { perDay: args.perDay },
    });
  });
  const dropSub = useMutation(async (sub: SubscriptionRow) => {
    await api.del(`/api/subscriptions/${sub.id}`);
  });
  const dropZone = useMutation(async (zoneId: string) => {
    await api.del(`/api/zones/${zoneId}`);
  });
  const toggleChannel = useMutation(async (args: { channelId: string; active: boolean }) => {
    await api.put(`/api/channels/${args.channelId}`, { active: args.active });
  });
  const dropChannel = useMutation(async (channelId: string) => {
    await api.del(`/api/channels/${channelId}`);
  });
  const removeClient = useMutation(async () => {
    await api.del(`/api/clients/${id}`);
  });

  if (detail.loading && !detail.data) return <LoadingState label="Loading client…" />;
  if (detail.error) return <ErrorBanner error={detail.error} onRetry={detail.reload} />;
  if (!detail.data) return null;

  const { client, subscriptions, channels } = detail.data;
  const subscribedMarketIds = new Set(subscriptions.map((s) => s.marketId));
  const availableMarkets = (markets.data?.items ?? []).filter((m) => !subscribedMarketIds.has(m.id));

  return (
    <Box>
      <PageHeader
        title={client.name}
        subtitle={
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={client.status}
              color={client.status === "active" ? "success" : "default"}
              variant="outlined"
            />
            <Typography variant="body2" color="text.secondary">
              Added {when(client.createdAt)}
            </Typography>
          </Stack>
        }
        actions={
          <>
            <Button component={RouterLink} to="/clients" startIcon={<ArrowBackIcon />}>
              All clients
            </Button>
            <RoleGate allowed={can("operator")} needs="operator">
              <Button startIcon={<EditIcon />} onClick={() => setEditingClient(true)}>
                Edit client
              </Button>
            </RoleGate>
            <RoleGate allowed={can("owner")} needs="owner">
              <Button color="error" startIcon={<DeleteIcon />} onClick={() => setDeletingClient(true)}>
                Delete
              </Button>
            </RoleGate>
          </>
        }
      />

      <ErrorBanner
        error={
          toggleSub.error ??
          setQuota.error ??
          dropSub.error ??
          dropZone.error ??
          toggleChannel.error ??
          dropChannel.error ??
          removeClient.error
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Market subscriptions</Typography>
          <RoleGate allowed={can("operator")} needs="operator">
            <Button
              startIcon={<AddIcon />}
              onClick={() => setSubscribing(true)}
              disabled={availableMarkets.length === 0}
            >
              Subscribe to a market
            </Button>
          </RoleGate>
        </Stack>

        {subscriptions.length === 0 ? (
          <Alert severity="info">
            Not subscribed to any market. Until it is, no lead can be routed to this client.
          </Alert>
        ) : null}

        <Stack spacing={2}>
          {subscriptions.map((sub) => (
            <Paper key={sub.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body1">
                    <Mono>{sub.marketKey}</Mono>{" "}
                    <Typography component="span" variant="body2" color="text.secondary">
                      {sub.marketRegion} · {sub.marketIndustry} · {sub.routingStrategy}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Joined {when(sub.joinedAt)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <QuotaField
                    value={sub.quota?.perDay ?? null}
                    disabled={!can("operator") || setQuota.pending}
                    onCommit={(perDay) =>
                      void setQuota.run({ sub, perDay }).then((ok) => {
                        if (ok) {
                          setToast("Quota updated");
                          detail.reload();
                        }
                      })
                    }
                  />
                  <RoleGate allowed={can("operator")} needs="operator">
                    <Tooltip title={sub.active ? "Pause this subscription" : "Resume (re-checks the cap)"}>
                      <Switch
                        checked={sub.active}
                        onChange={() => void toggleSub.run(sub).then((ok) => ok && detail.reload())}
                      />
                    </Tooltip>
                  </RoleGate>
                  <RoleGate allowed={can("operator")} needs="operator">
                    <IconButton
                      color="error"
                      onClick={() => void dropSub.run(sub).then((ok) => ok && detail.reload())}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </RoleGate>
                </Stack>
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" color="text.secondary">
                  Zones:
                </Typography>
                {sub.zones.length === 0 ? (
                  sub.routingStrategy === "geo_exclusive" ? (
                    <Chip label="none — nothing will be assigned" color="error" variant="outlined" />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      none (not needed for {sub.routingStrategy})
                    </Typography>
                  )
                ) : (
                  sub.zones.map((zone) => (
                    <Chip
                      key={zone.id}
                      label={`${zone.geoKind}:${zone.geoValue}`}
                      variant="outlined"
                      onDelete={
                        can("operator")
                          ? () => void dropZone.run(zone.id).then((ok) => ok && detail.reload())
                          : undefined
                      }
                    />
                  ))
                )}
                <RoleGate allowed={can("operator")} needs="operator">
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setZoneFor(sub)}>
                    Add zone
                  </Button>
                </RoleGate>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Paper>

      {/* Above delivery channels, and a peer of them: a channel is how leads are
          pushed at the client, a portal login is how they read them back. A client
          with no channel still has somewhere to look. */}
      <ClientUsersPanel
        clientId={client.id}
        clientName={client.name}
        roles={meta.data?.clientRoles}
      />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Delivery channels</Typography>
          <RoleGate allowed={can("operator")} needs="operator">
            <Button startIcon={<AddIcon />} onClick={() => setAddingChannel(true)}>
              Add channel
            </Button>
          </RoleGate>
        </Stack>

        {channels.length === 0 ? (
          <Alert severity="warning">
            No delivery channel. Assigned leads will be recorded but never sent anywhere.
          </Alert>
        ) : (
          <Stack spacing={1}>
            {channels.map((channel) => (
              <Stack
                key={channel.id}
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}
              >
                <Chip label={channel.type} variant="outlined" />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <ChannelSummary channel={channel} sender={sender} />
                </Box>
                <RoleGate allowed={can("operator")} needs="operator">
                  <Tooltip title="Edit this channel's configuration">
                    <IconButton onClick={() => setEditingChannel(channel)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </RoleGate>
                <RoleGate allowed={can("operator")} needs="operator">
                  <Switch
                    checked={channel.active}
                    onChange={() =>
                      void toggleChannel
                        .run({ channelId: channel.id, active: !channel.active })
                        .then((ok) => ok && detail.reload())
                    }
                  />
                </RoleGate>
                <RoleGate allowed={can("operator")} needs="operator">
                  <IconButton
                    color="error"
                    onClick={() => void dropChannel.run(channel.id).then((ok) => ok && detail.reload())}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </RoleGate>
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>

      {editingClient ? (
        <ClientDialog
          client={client}
          statuses={meta.data?.clientStatuses ?? ["active", "paused", "churned"]}
          onClose={() => setEditingClient(false)}
          onSaved={() => {
            setEditingClient(false);
            setToast("Client updated");
            detail.reload();
          }}
        />
      ) : null}

      {subscribing ? (
        <SubscribeDialog
          clientId={client.id}
          markets={availableMarkets}
          onClose={() => setSubscribing(false)}
          onSaved={() => {
            setSubscribing(false);
            setToast("Subscribed");
            detail.reload();
          }}
        />
      ) : null}

      {zoneFor ? (
        <ZoneDialog
          subscription={zoneFor}
          geoKinds={meta.data?.geoKinds ?? ["state", "city", "borough", "zip"]}
          onClose={() => setZoneFor(null)}
          onSaved={() => {
            setZoneFor(null);
            setToast("Zone added");
            detail.reload();
          }}
        />
      ) : null}

      {addingChannel ? (
        <ChannelDialog
          clientId={client.id}
          channelTypes={meta.data?.channelTypes ?? ["webhook", "slack", "email", "log"]}
          sender={sender}
          onClose={() => setAddingChannel(false)}
          onSaved={() => {
            setAddingChannel(false);
            setToast("Channel added");
            detail.reload();
          }}
        />
      ) : null}

      {editingChannel ? (
        <ChannelDialog
          clientId={client.id}
          channel={editingChannel}
          channelTypes={meta.data?.channelTypes ?? ["webhook", "slack", "email", "log"]}
          sender={sender}
          onClose={() => setEditingChannel(null)}
          onSaved={() => {
            setEditingChannel(null);
            setToast("Channel updated");
            detail.reload();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={deletingClient}
        title={`Delete ${client.name}?`}
        pending={removeClient.pending}
        body="Deleting a client discards its lead assignments and reported outcomes — the trial scoreboard. Setting the status to churned keeps them."
        onCancel={() => setDeletingClient(false)}
        onConfirm={() => {
          void removeClient.run(undefined).then((ok) => {
            if (!ok) return;
            setDeletingClient(false);
            navigate("/clients", { replace: true });
          });
        }}
      />

      <Snackbar open={toast !== null} autoHideDuration={4000} onClose={() => setToast(null)} message={toast ?? ""} />
    </Box>
  );
}

/**
 * What a channel is configured to do, in one or two lines.
 *
 * The HTTP channels have nothing safe to show but the host — the rest of the URL is
 * the credential. An email channel is the opposite: the recipients, sender and
 * relay are the useful, non-secret part, and it is worth reading them off the row
 * without opening the form, because "which mailbox does this client actually get
 * leads in" is the question this screen exists to answer.
 */
function ChannelSummary({ channel, sender }: { channel: ChannelRow; sender: GmailSenderState }) {
  if (channel.type === "email") {
    const email = readEmailChannel(channel.extra);

    if (email.provider === "gmail_oauth") {
      const connected = sender.kind === "connected";
      return (
        <>
          {connected ? null : (
            <Typography variant="body2" color="error">
              No Google account is connected for this server — nothing will be delivered until one
              is. Connect it in Settings → Email sender.
            </Typography>
          )}
          <Typography variant="body2" noWrap>
            To <Mono>{email.to.length > 0 ? email.to.join(", ") : "nobody"}</Mono>
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {connected ? `Gmail · sends as ${sender.account}` : "Gmail · no sender connected"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            The sender is this deployment's single Google account, shared by every Gmail channel. Its
            authorization is stored encrypted and is never returned to the browser: this server can
            send as the account, not read its mail.
          </Typography>
          {email.legacyAccount ? (
            <Typography variant="caption" color="text.secondary" display="block">
              Leftover from before the sender was server-wide: this row still names{" "}
              <Mono>{email.legacyAccount}</Mono>, which is not used. Saving the channel clears it.
            </Typography>
          ) : null}
        </>
      );
    }

    const incomplete = email.host === null || email.from === null || email.to.length === 0;
    return (
      <>
        {incomplete ? (
          <Typography variant="body2" color="error">
            Incomplete SMTP configuration — nothing will be delivered until it is fixed.
          </Typography>
        ) : null}
        <Typography variant="body2" noWrap>
          To <Mono>{email.to.length > 0 ? email.to.join(", ") : "nobody"}</Mono>
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {`From ${email.fromName ? `${email.fromName} <${email.from ?? "?"}>` : (email.from ?? "not set")}`}
          {` · relay ${email.host ?? "not set"}:${email.port ?? "?"} (${email.tls ?? "starttls"})`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          The SMTP username and password are write-only: the API returns neither their values nor whether
          one is stored. Saving a blank pair keeps whatever is there.
        </Typography>
      </>
    );
  }

  return (
    <>
      <Typography variant="body2">
        {channel.hasUrl ? (
          <>Destination stored{channel.urlHost ? ` · ${channel.urlHost}` : ""}</>
        ) : channel.type === "log" ? (
          "Logs only — no destination needed"
        ) : (
          "No destination configured"
        )}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        The URL is never returned to the browser. Replace it by saving a new one.
      </Typography>
    </>
  );
}

/** Inline quota editor; commits on blur so there is no separate save button per row. */
function QuotaField({
  value,
  disabled,
  onCommit,
}: {
  value: number | null;
  disabled: boolean;
  onCommit: (perDay: number | null) => void;
}) {
  const [text, setText] = useState(value === null ? "" : String(value));
  return (
    <TextField
      label="Leads/day"
      value={text}
      disabled={disabled}
      onChange={(e) => setText(e.target.value.replace(/\D/g, ""))}
      onBlur={() => {
        const next = text.trim() === "" ? null : Number(text);
        if (next !== value) onCommit(next);
      }}
      helperText="blank = no cap"
      sx={{ width: 120 }}
    />
  );
}

/**
 * Rename a client, or move its status.
 *
 * Only those two fields, because they are the only two the API accepts on a client
 * row: its markets, zones and channels are separate objects with their own controls
 * on this screen. Status is the one that changes behaviour — a paused or churned
 * client is skipped by the router — so it is worth reaching without deleting
 * anything.
 */
function ClientDialog({
  client,
  statuses,
  onClose,
  onSaved,
}: {
  client: ClientDetail["client"];
  statuses: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(client.name);
  const [status, setStatus] = useState(client.status);

  // A status the server has but this build's meta does not would otherwise render as
  // an empty select and be silently overwritten on save.
  const options = statuses.includes(client.status) ? statuses : [client.status, ...statuses];

  const save = useMutation(async () => {
    await api.put(`/api/clients/${client.id}`, { name: name.trim(), status });
  });

  const unchanged = name.trim() === client.name && status === client.status;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit client</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            fullWidth
          />
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            helperText="Only an active client is assigned leads. churned keeps its assignments and outcomes; deleting does not."
            fullWidth
          >
            {options.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={name.trim() === "" || unchanged || save.pending}
          onClick={() => void save.run(undefined).then((ok) => ok && onSaved())}
        >
          {save.pending ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SubscribeDialog({
  clientId,
  markets,
  onClose,
  onSaved,
}: {
  clientId: string;
  markets: MarketRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [marketId, setMarketId] = useState("");
  const [perDay, setPerDay] = useState("");
  const selected = markets.find((m) => m.id === marketId);

  const save = useMutation(async () => {
    await api.post("/api/subscriptions", {
      clientId,
      marketId,
      quota: perDay.trim() === "" ? {} : { perDay: Number(perDay) },
    });
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Subscribe to a market</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Market"
            value={marketId}
            onChange={(e) => setMarketId(e.target.value)}
            fullWidth
          >
            {markets.map((m) => (
              <MenuItem key={m.id} value={m.id} disabled={m.seatsLeft === 0}>
                {m.key} — {m.seatsLeft ?? "?"} seat(s) left
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Daily quota"
            value={perDay}
            onChange={(e) => setPerDay(e.target.value.replace(/\D/g, ""))}
            helperText="Leads per day. Blank means no cap."
            sx={{ width: 180 }}
          />
          {selected?.routingStrategy === "geo_exclusive" ? (
            <Alert severity="info">
              This market is geo-exclusive — add at least one zone after subscribing, or this client will never
              be assigned a lead.
            </Alert>
          ) : null}
          {selected?.seatsLeft === 0 ? (
            <Alert severity="error">This market is at capacity. Raise the cap or free a seat first.</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={marketId === "" || save.pending}
          onClick={() => void save.run(undefined).then((ok) => ok && onSaved())}
        >
          {save.pending ? "Saving…" : "Subscribe"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ZoneDialog({
  subscription,
  geoKinds,
  onClose,
  onSaved,
}: {
  subscription: SubscriptionRow;
  geoKinds: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [geoKind, setGeoKind] = useState("city");
  const [geoValue, setGeoValue] = useState("");

  const save = useMutation(async () => {
    await api.post(`/api/subscriptions/${subscription.id}/zones`, { geoKind, geoValue });
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add zone to {subscription.marketKey}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select label="Kind" value={geoKind} onChange={(e) => setGeoKind(e.target.value)} fullWidth>
            {geoKinds.map((k) => (
              <MenuItem key={k} value={k}>
                {k}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Value"
            value={geoValue}
            onChange={(e) => setGeoValue(e.target.value)}
            helperText={
              geoKind === "zip"
                ? "Exact zip, e.g. 02118"
                : "Stored uppercase to match the router, e.g. BOSTON"
            }
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={geoValue.trim() === "" || save.pending}
          onClick={() => void save.run(undefined).then((ok) => ok && onSaved())}
        >
          {save.pending ? "Saving…" : "Add zone"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Add or edit one delivery channel.
 *
 * One dialog for both, because the fields are the same either way — the only
 * difference is what a blank credential box means. On create it means "no
 * credential"; on edit it means "keep the one the server never showed me", which is
 * exactly how the API reads an omitted secret. The type is fixed once created: each
 * type's config is a different shape, so switching would need a new row, and the
 * server would be merging a URL into an SMTP transport.
 *
 * There is no Connect Gmail button here. The sending account is one server-wide
 * connection an owner makes on /settings; this dialog decides who a client's leads
 * are addressed to, and shows the sender as status so the two are not confused.
 */
function ChannelDialog({
  clientId,
  channel,
  channelTypes,
  sender,
  onClose,
  onSaved,
}: {
  clientId: string;
  /** Present = edit that channel; absent = add a new one. */
  channel?: ChannelRow;
  channelTypes: string[];
  /** The deployment's Gmail sender. Read-only from here. */
  sender: GmailSenderState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = channel !== undefined;
  const [type, setType] = useState(channel?.type ?? "webhook");
  const [url, setUrl] = useState("");
  const stored = channel?.type === "email" ? readEmailChannel(channel.extra) : undefined;
  const [email, setEmail] = useState<EmailFormState>(() => emailFormState(stored));

  const check = validateEmailForm(email, editing ? "edit" : "create");

  const emailExtra = () => emailChannelExtra(email, check.recipients);

  /** Write the channel, creating it if this is the Add dialog. */
  const persist = async (): Promise<void> => {
    const extra = type === "email" ? emailExtra() : undefined;
    if (channel !== undefined) {
      // A blank URL box on an edit means "keep the stored one", so the key is left
      // out entirely — sending "" is rejected as a missing destination.
      const body =
        type === "email"
          ? { extra }
          : type !== "log" && url.trim() !== ""
            ? { url: url.trim() }
            : {};
      await api.put(`/api/channels/${channel.id}`, body);
      return;
    }
    await api.post<{ id: string }>(`/api/clients/${clientId}/channels`, {
      type,
      // An email channel is configured entirely through `extra`; the API rejects a
      // `url` on one, since there is no single destination to put in it.
      ...(type === "email" ? { extra } : {}),
      ...(type === "webhook" || type === "slack" ? { url: url.trim() } : {}),
    });
  };

  const save = useMutation(persist);

  const busy = save.pending;
  const blocked = type === "email" ? !check.ok : !editing && type !== "log" && url.trim() === "";

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? `Edit ${type} channel` : "Add delivery channel"}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            disabled={editing}
            fullWidth
            helperText={
              editing
                ? "The type cannot be changed — each one stores a different shape of config. Delete and re-add instead."
                : undefined
            }
          >
            {channelTypes.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>

          {type === "log" ? (
            <Alert severity="info">
              The log channel records a delivery without sending anything. Useful for a dry run.
            </Alert>
          ) : type === "email" ? (
            <EmailChannelFields
              state={email}
              onChange={setEmail}
              check={check}
              mode={editing ? "edit" : "create"}
              sender={sender}
              legacyAccount={stored?.legacyAccount ?? null}
            />
          ) : (
            <>
              <SecretField
                label={type === "slack" ? "Slack incoming webhook URL" : "Webhook URL"}
                value={url}
                onChange={setUrl}
                isSet={editing && (channel?.hasUrl ?? false)}
                required={!editing}
                helperText={
                  (type === "slack"
                    ? "Must be a hooks.slack.com URL. Anyone holding it can post to the channel, so it is stored write-only."
                    : "Must be https. Stored write-only — it is never returned to the browser.") +
                  (editing
                    ? channel?.hasUrl
                      ? " A URL is stored — leave this blank to keep it."
                      : " Nothing is stored yet."
                    : "")
                }
              />
              <Alert severity="warning">
                Treat this URL as a credential. Once saved it cannot be read back, only replaced.
              </Alert>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={blocked || busy}
          onClick={() => void save.run(undefined).then((ok) => ok && onSaved())}
        >
          {save.pending ? "Saving…" : editing ? "Save changes" : "Add channel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
